import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../layout/TopBar';
import { routeBase, studyName, studyThumbnail, useCollectionMutations, useOpenStudy } from '../api/collections';
import { publicThumbnail, useExplore, type ExploreCard } from '../api/explore';
import { AuthorChip } from '../ui/Avatar';
import { Cover, MosaicCard, MosaicMini, StudyCard } from '../ui/Cards';
import { Chip, Segmented } from '../ui/Controls';
import { IconHeart } from '../ui/icons';
import { plainText } from '../ui/RichText';
import { toast } from '../ui/toast';

/**
 * Explore (`is.explore`): organ chips, the sort control, the featured hero
 * and the trending list (only when unfiltered), the collections grid, the
 * public studies grid. Guests get the same page from /public.
 */
type Sort = 'trending' | 'saves' | 'recent';
const SORTS: Array<{ id: Sort; label: string }> = [{ id: 'trending', label: 'Trending' }, { id: 'saves', label: 'Most saved' }, { id: 'recent', label: 'Newest' }];

export function ExplorePage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: me, isLoading } = useSession();
  const guest = isLoading ? undefined : !me?.userId;
  const organ = params.get('organ') || 'All';
  const sort = (params.get('sort') as Sort) || 'trending';
  const q = params.get('q') || '';
  const { data } = useExplore(guest, { organ, sort, q });
  const m = useCollectionMutations();
  const openStudy0 = useOpenStudy();
  const thumb = (studyId: string) => (guest ? publicThumbnail(studyId) : studyThumbnail(studyId));
  const set = (k: string, v: string, dflt: string) => {
    const p = new URLSearchParams(params);
    if (v && v !== dflt) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  const all = data?.collections ?? [];
  const organs = ['All', ...(data?.organs ?? [])];
  const showFeatured = !q && organ === 'All';
  const featured = showFeatured && all.length ? all.reduce((a, b) => (b.like_count > a.like_count ? b : a), all[0]) : null;
  // Five rows, as the bundle: the list beside the featured block sets their shared height.
  const trending = [...all].sort((a, b) => b.recent_likes - a.recent_likes || b.like_count - a.like_count).slice(0, 5);
  const open = (c: ExploreCard) => nav(`${routeBase()}/collections/${c.public_id}`);
  const openStudy = (studyId: string) => {
    if (guest) {
      toast('Create a free account to open studies', { label: 'Create account', fn: () => nav('/signup') });
      return;
    }
    openStudy0(studyId);
  };
  const like = (c: ExploreCard) => {
    if (guest) {
      toast('Create a free account to save collections', { label: 'Create account', fn: () => nav('/signup') });
      return;
    }
    m.like.mutate({ kind: 'collection', id: c.public_id, liked: c.liked });
  };
  const listTitle = organ === 'All' ? (q ? 'Results' : 'Collections') : `${organ} collections`;

  return (
    <div className="tr-page" style={{ padding: '36px 28px 44px', display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 1440, width: '100%', margin: '0 auto', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 760 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: '-.02em' }}>Explore</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#6b6b70', lineHeight: 1.5, textWrap: 'pretty' }}>
            Collections and studies shared by pathologists everywhere.
            {guest ? (
              <>
                {' '}Browse without an account, or{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); nav('/signup'); }} style={{ display: 'inline', color: '#0f0f10', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap' }}>
                  create a free account
                </a>{' '}
                to save, comment and share your own.
              </>
            ) : null}
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {organs.map((o) => <Chip key={o} label={o} on={organ === o} onClick={() => set('organ', o, 'All')} />)}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Segmented value={sort} options={SORTS} onChange={(v) => set('sort', v, 'trending')} />
        </div>
      </div>

      {featured ? (
        // As the bundle: the hero is as tall as the five-row trending list
        // beside it (300px at least), one large cover and two stacked. The
        // covers sit in their own layer pinned to the card: slide thumbnails
        // are tall images, and in the flow they set the card's height (1234px
        // for a portrait slide at 1440px wide).
        <div className="tr-stack" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)', gap: 20 }}>
          <div className="tr-hover-ring" onClick={() => open(featured)} style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', minHeight: 300, background: '#fff', border: '1px solid #ebebea' }}>
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)', gridTemplateRows: 'minmax(0,1fr) minmax(0,1fr)', gap: 2 }}>
              <Cover src={featured.covers[0] ? thumb(featured.covers[0]) : undefined} fallback="#f2f2f0" style={{ gridRow: '1 / 3' }} />
              <Cover src={featured.covers[1] ? thumb(featured.covers[1]) : undefined} fallback="#e8e8e6" />
              <Cover src={featured.covers[2] ? thumb(featured.covers[2]) : undefined} fallback="#e0e0de" />
            </div>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.72) 100%)' }} />
            <div style={{ position: 'absolute', top: 14, left: 14, height: 22, padding: '0 8px', borderRadius: 5, background: 'rgba(255,255,255,.92)', fontSize: 11, fontWeight: 600, letterSpacing: '.05em', lineHeight: '22px' }}>FEATURED</div>
            <div style={{ position: 'absolute', left: 20, right: 20, bottom: 18, display: 'flex', flexDirection: 'column', gap: 6, color: '#fff' }}>
              <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.01em' }}>{featured.name}</span>
              <span className="tr-clamp-2" style={{ fontSize: 13.5, lineHeight: 1.5, opacity: 0.9, maxWidth: 560 }}>{plainText(featured.description)}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, opacity: 0.9, marginTop: 2, flexWrap: 'wrap' }}>
                {featured.author ? <AuthorChip author={featured.author} color="#fff" fontSize={12.5} style={{ flex: 'none' }} /> : null}
                <span>·</span>
                <span style={{ whiteSpace: 'nowrap' }}>{featured.studies} studies</span>
                <span>·</span>
                <span style={{ whiteSpace: 'nowrap' }}>♥ {featured.like_count}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '20px 22px', border: '1px solid #ebebea', borderRadius: 14, background: '#fafaf9' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', lineHeight: 'normal' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Trending this week</span>
              <span style={{ fontSize: 12, color: '#8a8a8f' }}>by saves</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {trending.map((t, i) => (
                <div key={t.public_id} className="tr-hover-track" onClick={() => open(t)} style={{ display: 'grid', gridTemplateColumns: '22px 56px minmax(0,1fr) auto', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid #ebebea', cursor: 'pointer' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#8a8a8f', fontFamily: "'JetBrains Mono', monospace" }}>{String(i + 1).padStart(2, '0')}</span>
                  <MosaicMini c1={t.covers[0] ? thumb(t.covers[0]) : undefined} c2={t.covers[1] ? thumb(t.covers[1]) : undefined} c3={t.covers[2] ? thumb(t.covers[2]) : undefined} w={56} h={40} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#8a8a8f', minWidth: 0 }}>
                      {t.author ? <AuthorChip author={t.author} size={14} color="#8a8a8f" style={{ flex: 'none' }} /> : null}
                    </div>
                  </div>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: '#6b6b70' }}>
                    <IconHeart size={12} />
                    {t.like_count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{listTitle}</h2>
          <span style={{ fontSize: 13, color: '#8a8a8f' }}>{all.length}</span>
        </div>
        {data && all.length === 0 ? <div style={{ padding: '28px 16px', border: '1px solid #ebebea', borderRadius: 10, fontSize: 13, color: '#8a8a8f', textAlign: 'center' }}>Nothing matches. Try another organ or clear the search.</div> : null}
        <div className="tr-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px 20px' }}>
          {all.map((c) => (
            <MosaicCard
              key={c.public_id}
              c1={c.covers[0] ? thumb(c.covers[0]) : undefined}
              c2={c.covers[1] ? thumb(c.covers[1]) : undefined}
              c3={c.covers[2] ? thumb(c.covers[2]) : undefined}
              count={c.studies}
              vis="none"
              title={c.name}
              desc={plainText(c.description)}
              meta={c.organ ?? ''}
              author={c.author ?? undefined}
              likes={c.like_count}
              liked={c.liked}
              onOpen={() => open(c)}
              onToggleLike={() => like(c)}
            />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 28, borderTop: '1px solid #ebebea' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Studies</h2>
          <span style={{ fontSize: 13, color: '#8a8a8f' }}>{data?.studies.length ?? 0}</span>
        </div>
        <div className="tr-cols-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 18 }}>
          {(data?.studies ?? []).map((s) => (
            <StudyCard
              key={s.public_id}
              cover={thumb(s.public_id)}
              count={s.images}
              vis="none"
              name={studyName(s)}
              desc={s.description ?? ''}
              meta={s.collection_name ?? undefined}
              author={s.author ?? undefined}
              onOpen={() => openStudy(s.public_id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
